import { Tag } from "entities/tags/types";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import {
  useHits,
  useConfigure,
  useSearchBox,
  useInstantSearch,
} from "react-instantsearch";

export const ALGOLIA_INDEX_NAME =
  process.env.REACT_APP_ALGOLIA_THEME_TAGS_INDEX_NAME || "";

const initialPaginationModal = {
  pageSize: 25,
  page: 0,
};

export const useSearchThemeTagsData = ({
  tagTypesIds = [],
}: {
  tagTypesIds: string[];
}) => {
  const [paginationModel, setPaginationModel] = useState(
    initialPaginationModal,
  );

  const { query, refine: refineSearch, clear } = useSearchBox();
  const { hits, results } = useHits<Tag>();
  const { status } = useInstantSearch();

  // Clear search query on unmount custom hook
  useEffect(() => {
    return () => {
      clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onResetPage = () =>
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

  // Reset page when filter values change
  useEffect(() => {
    onResetPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tagTypesIds)]);

  // Filters settings
  const tagTypeIdFilter =
    tagTypesIds.length > 0
      ? `(${tagTypesIds.map((id) => `tagType:${id}`).join(" OR ")})`
      : "";

  const filters = [tagTypeIdFilter].filter(Boolean).join(" AND ");

  // Use useConfigure to apply dynamic filters and pagination
  useConfigure({
    query,
    hitsPerPage: paginationModel.pageSize,
    page: paginationModel.page,
    filters,
  });

  // Normalized table list data
  const data = useMemo(
    () =>
      hits.map((hit) => ({
        ...hit,
        id: hit.objectID,
      })),
    [hits],
  );

  const onSearch = debounce((value) => {
    refineSearch(value);
    onResetPage();
  }, 500);

  const { nbHits = 0 } = results || {};
  const isLoading = status === "loading";

  return {
    searchData: {
      value: query,
      onChange: onSearch,
    },
    tableData: {
      data,
      rowCount: nbHits,
      paginationModel: paginationModel,
      onPaginationModelChange: setPaginationModel,
    },
    isLoading,
    onResetPage,
  };
};
